import classNames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as ChatIcon } from '../../assets/images/icons/chat_outline_24.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { ReactComponent as LockIcon } from '../../assets/images/icons/lock_outline_24.svg';
import { useCartPricingError, useCartPricingRefreshing } from '../../contexts/CartPricingContext';
import useAnimatedNumber from '../../hooks/useAnimatedNumber';
import useFreeShippingDisplay from '../../hooks/useFreeShippingDisplay';
import useHardwareFirstTrialOnlyCart from '../../hooks/useHardwareFirstTrialOnlyCart';
import CheckoutContext from '../../lib/CheckoutContext';
import { isAmountGiftCardOnlyCart } from '../../lib/hardwareFirstGiftPlans';
import * as types from '../../types';
import GiftCheckbox from '../HardwareFirstGift/GiftCheckbox';
import giftStyles from '../HardwareFirstGift/GiftCheckbox.module.scss';
import { giftPlanRowLabel } from '../HardwareFirstGift/giftTermLabelKeys';
import { useCartCheckout } from '../CartSummary/CartActions';
import HardwareFirstCartPromo from './HardwareFirstCartPromo';
import useHardwareFirstTotals from './useHardwareFirstTotals';
import styles from './HardwareFirstOrderSummary.module.scss';

const HELP_CENTER_LINK = (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a className={styles.helpLink} href="https://support.fitracking.com" rel="noopener noreferrer" target="_blank" />
);

interface HardwareFirstOrderSummaryProps {
  onCheckOut: (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
  /** Off on mobile, where the code sits in its own card under the items instead. */
  showPromo?: boolean;
}

/**
 * The hardware-first receipt: what the hardware lists at, what came off it, and the pre-tax
 * subtotal, then the checkout button, the promo code and the returns and support reassurances
 * closing the same card. Tax lands at checkout, so this total is a subtotal rather than a final
 * amount. Desktop checks out from the button here; mobile hides it and uses the pinned bar instead.
 */
export default function HardwareFirstOrderSummary({ onCheckOut, showPromo = true }: HardwareFirstOrderSummaryProps) {
  const { t } = useTranslation();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();
  const {
    trackerCount,
    hardwareInCents,
    accessoriesInCents,
    giftCardInCents,
    trialInCents,
    promoDiscountInCents,
    multiDeviceDiscountInCents,
    giftPlanInCents,
    giftPlanListInCents,
    giftTermMonths,
    totalInCents,
    formatPrice: formatTotal,
  } = useHardwareFirstTotals();
  const pricingError = useCartPricingError();
  const formatPrice = (cents: number) => (pricingError ? '—' : formatTotal(cents));
  // a cart of only a trial has nothing a code could take off, so the field and its rows stay away
  const trialOnly = useHardwareFirstTrialOnlyCart();
  const checkout = useCartCheckout(onCheckOut);
  const refreshing = useCartPricingRefreshing();
  const { cart } = useContext(CheckoutContext);
  const cardOnly = isAmountGiftCardOnlyCart(cart);
  // the roll waits for the pending fade to clear, so the number moves once the server has spoken
  const shownTotalInCents = useAnimatedNumber(totalInCents, !refreshing);
  // the chip pops only when a saving first appears, not on every repaint while one is showing
  const savingInCents = pricingError ? 0 : promoDiscountInCents + multiDeviceDiscountInCents;
  const [savingPops, setSavingPops] = useState(false);
  const lastSaving = useRef(savingInCents);
  useEffect(() => {
    if (lastSaving.current === 0 && savingInCents > 0) setSavingPops(true);
    lastSaving.current = savingInCents;
  }, [savingInCents]);
  const pendingValue = classNames(styles.rowValue, { [styles.pending]: refreshing });

  return (
    <div className={styles.summary}>
      <div className={styles.card}>
        <h2 className={styles.title}>{t('hardware_first_cart_order_summary')}</h2>
        <div className={styles.rows} aria-busy={refreshing}>
          {!cardOnly && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>
                {trackerCount === 1
                  ? t('hardware_first_cart_hardware_one')
                  : t('hardware_first_cart_hardware_other', { count: trackerCount })}
              </span>
              <span className={styles.rowValue}>{formatPrice(hardwareInCents)}</span>
            </div>
          )}
          {accessoriesInCents > 0 && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>{t('accessories')}</span>
              <span className={styles.rowValue}>{formatPrice(accessoriesInCents)}</span>
            </div>
          )}
          {giftCardInCents > 0 && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>{t('fi_gift_card')}</span>
              <span className={styles.rowValue}>{formatPrice(giftCardInCents)}</span>
            </div>
          )}
          {trialInCents > 0 && (
            <div className={classNames(styles.row, styles.discountRow)}>
              <span className={styles.rowLabel}>{t('hardware_first_cart_on_trial')}</span>
              <span className={pendingValue}>{t('negative_amount', { amount: formatPrice(trialInCents) })}</span>
            </div>
          )}
          {promoDiscountInCents > 0 && (
            <div className={classNames(styles.row, styles.discountRow)}>
              <span className={styles.rowLabel}>{cart.couponCode ?? t('discount')}</span>
              <span className={pendingValue}>
                {t('negative_amount', { amount: formatPrice(promoDiscountInCents) })}
              </span>
            </div>
          )}
          {multiDeviceDiscountInCents > 0 && (
            <div className={classNames(styles.row, styles.discountRow)}>
              <span className={styles.rowLabel}>{t('hardware_first_cart_multi_device_discount')}</span>
              <span className={pendingValue}>
                {t('negative_amount', { amount: formatPrice(multiDeviceDiscountInCents) })}
              </span>
            </div>
          )}
          {giftTermMonths && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>{giftPlanRowLabel(t, giftTermMonths, trackerCount)}</span>
              <span className={styles.rowValue}>
                {giftPlanListInCents > giftPlanInCents && (
                  <s className={giftStyles.was}>{formatPrice(giftPlanListInCents)}</s>
                )}
                {formatPrice(giftPlanInCents)}
              </span>
            </div>
          )}
          {!cardOnly && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>{t('hardware_first_cart_ground_shipping')}</span>
              <span className={styles.rowValue}>
                {isFreeShipping ? t('cart_item_v2.price_free') : pricingError ? '—' : shippingPrice}
              </span>
            </div>
          )}
          <div className={styles.divider} />
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>{t('subtotal')}</span>
            <span className={classNames(styles.totalValue, { [styles.pending]: refreshing })}>
              {formatPrice(shownTotalInCents)}
            </span>
          </div>
          {savingInCents > 0 && (
            <div
              className={classNames(styles.saving, { [styles.savingPop]: savingPops })}
              onAnimationEnd={() => setSavingPops(false)}
            >
              <span className={styles.savingTick}>
                <CheckIcon />
              </span>
              <span>{t('hardware_first_cart_you_are_saving', { amount: formatPrice(savingInCents) })}</span>
            </div>
          )}
          <p className={styles.taxNote}>{t('hardware_first_cart_taxes_at_checkout')}</p>
        </div>
        <GiftCheckbox />
        <button className={styles.checkoutButton} onClick={checkout} type="button">
          {t('checkout')}
        </button>
        {/* the code goes in under the button: desktop checks out from this card, and the yellow
            offer pill must not sit between the subtotal and the checkout button */}
        {showPromo && !trialOnly && !cardOnly && (
          <div className={styles.promo}>
            <HardwareFirstCartPromo />
          </div>
        )}
      </div>
      <div className={styles.reassurances}>
        <div className={styles.reassurance}>
          <span className={styles.reassuranceIcon}>
            <LockIcon />
          </span>
          <span className={styles.reassuranceText}>
            <span className={styles.reassuranceTitle}>{t('hardware_first_cart_try_it_risk_free')}</span>
            <span className={styles.reassuranceBody}>{t('hardware_first_cart_returns_within_30_days')}</span>
          </span>
        </div>
        <div className={styles.reassurance}>
          <span className={styles.reassuranceIcon}>
            <ChatIcon />
          </span>
          <span className={styles.reassuranceText}>
            <span className={styles.reassuranceTitle}>{t('hardware_first_cart_questions_title')}</span>
            <span className={styles.reassuranceBody}>
              <Trans i18nKey="hardware_first_cart_questions_help_center" components={{ helpLink: HELP_CENTER_LINK }} />
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
